import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileDown, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { GlassCard, PageHeader, StatusBadge } from "../components/shared";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const LETTERS = [
    { key: "offer_letter", label: "Offer Letter" },
    { key: "joining_letter", label: "Joining Letter" },
    { key: "internship_letter", label: "Internship Letter" },
    { key: "experience_certificate", label: "Experience Certificate" },
    { key: "relieving_letter", label: "Relieving Letter" },
];

export default function EmployeeProfile() {
    const { id } = useParams();
    const { user } = useAuth();
    const [emp, setEmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingLetter, setSavingLetter] = useState("");
    const canManage = ["super_admin", "hr"].includes(user?.role);

    useEffect(() => {
        api.get(`/employees/${id}`).then((r) => setEmp(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    }, [id]);

    const update = async (patch) => {
        try {
            const { data } = await api.patch(`/employees/${id}`, patch);
            setEmp(data);
            toast.success("Saved");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const generateLetter = async (type) => {
        setSavingLetter(type);
        try {
            const res = await api.post(`/documents/generate-letter`, { employee_id: id, letter_type: type }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${type}_${emp.name}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success(`${type.replace("_", " ")} generated`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingLetter(""); }
    };

    if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin w-6 h-6 mx-auto text-primary" /></div>;
    if (!emp) return <div>Not found</div>;

    return (
        <div>
            <Link to="/employees" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>

            <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
                {emp.avatar ? (
                    <img src={emp.avatar} alt={emp.name} className="w-20 h-20 rounded-2xl object-cover border-2 border-teal-500 shadow-md" />
                ) : (
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent grid place-items-center text-white text-3xl font-heading font-bold">{emp.name[0]?.toUpperCase()}</div>
                )}
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="font-heading text-3xl font-bold tracking-tight">{emp.name}</h1>
                        <StatusBadge status={emp.status} />
                    </div>
                    <div className="text-muted-foreground text-sm mt-1">{emp.designation || "—"} · {emp.department || "—"} · {emp.email}</div>
                    <div className="text-xs text-muted-foreground mt-1">Employee ID: <span className="font-mono">{emp.employee_id}</span></div>
                </div>
            </div>

            <Tabs defaultValue="profile">
                <TabsList>
                    <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
                    {canManage && <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding Form</TabsTrigger>}
                    <TabsTrigger value="letters" data-testid="tab-letters">Letters</TabsTrigger>
                </TabsList>
                <TabsContent value="profile" className="mt-4">
                    <GlassCard className="p-6">
                        <ProfileForm emp={emp} canManage={canManage} onSave={update} />
                    </GlassCard>
                </TabsContent>
                {canManage && (
                    <TabsContent value="onboarding" className="mt-4">
                        <GlassCard className="p-6">
                            <OnboardingFormView emp={emp} />
                        </GlassCard>
                    </TabsContent>
                )}
                <TabsContent value="letters" className="mt-4">
                    <GlassCard className="p-6">
                        <h3 className="font-heading font-semibold mb-4">Generate Letters</h3>
                        {!canManage ? (
                            <p className="text-sm text-muted-foreground">Only HR or admins can generate letters.</p>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {LETTERS.map((l) => (
                                    <Button
                                        key={l.key}
                                        variant="outline"
                                        onClick={() => generateLetter(l.key)}
                                        disabled={savingLetter === l.key}
                                        className="rounded-xl h-auto py-4 flex flex-col gap-1"
                                        data-testid={`gen-${l.key}`}
                                    >
                                        {savingLetter === l.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                                        <span className="text-sm">{l.label}</span>
                                    </Button>
                                ))}
                            </div>
                        )}
                    </GlassCard>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function ProfileForm({ emp, canManage, onSave }) {
    const [form, setForm] = useState({
        name: emp.name || "",
        phone: emp.phone || "",
        department: emp.department || "",
        designation: emp.designation || "",
        joining_date: emp.joining_date || "",
        salary: emp.salary || 0,
        reporting_manager: emp.reporting_manager || "",
        skills: (emp.skills || []).join(", "),
        notes: emp.notes || "",
    });

    const submit = (e) => {
        e.preventDefault();
        onSave({
            ...form,
            salary: Number(form.salary) || 0,
            skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        });
    };

    const fields = [
        ["Name", "name"],
        ["Phone", "phone"],
        ["Department", "department"],
        ["Designation", "designation"],
        ["Joining date", "joining_date", "date"],
        ["Salary", "salary", "number"],
        ["Reporting manager", "reporting_manager"],
    ];

    return (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map(([label, key, type]) => (
                <div key={key}>
                    <Label>{label}</Label>
                    <Input type={type || "text"} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} disabled={!canManage} className="mt-1" data-testid={`profile-${key}`} />
                </div>
            ))}
            <div className="md:col-span-2">
                <Label>Skills (comma separated)</Label>
                <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} disabled={!canManage} className="mt-1" />
            </div>
            <div className="md:col-span-2">
                <Label>Notes</Label>
                <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={!canManage}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                />
            </div>
            {canManage && (
                <div className="md:col-span-2">
                    <Button type="submit" className="rounded-full" data-testid="profile-save">Save changes</Button>
                </div>
            )}
        </form>
    );
}

function OnboardingFormView({ emp }) {
    const data = emp.joining_form_data;
    const status = emp.joining_form_status || "not_started";
    
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-sm text-muted-foreground">This employee has not started their onboarding formalities yet.</p>
                <div className="mt-2 text-xs font-semibold text-slate-400">Status: Not Started</div>
            </div>
        );
    }
    
    const renderSection = (title, fields) => {
        return (
            <div className="border-b last:border-0 pb-6 mb-6 last:pb-0 last:mb-0">
                <h4 className="font-heading text-xs font-bold text-teal-600 dark:text-teal-400 mb-3">{title}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {fields.map(([label, val]) => (
                        <div key={label} className="min-w-0">
                            <span className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{label}</span>
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 break-words mt-0.5 block">
                                {val || <span className="text-slate-400 italic">Not provided</span>}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
                <h3 className="font-heading font-semibold text-sm">Onboarding Submission Details</h3>
                <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border
                    ${status === "submitted" 
                        ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400"
                        : "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400"
                    }`}>
                    {status === "submitted" ? "Submitted & Locked" : "Draft (In Progress)"}
                </span>
            </div>
            
            {data.personal && renderSection("1. Personal Details", [
                ["Full Name", data.personal.fullName],
                ["Father Name", data.personal.fatherName],
                ["Mother Name", data.personal.motherName],
                ["DOB", data.personal.dob],
                ["Gender", data.personal.gender],
                ["Marital Status", data.personal.maritalStatus],
                ["Blood Group", data.personal.bloodGroup],
                ["Nationality", data.personal.nationality],
                ["Aadhaar Card No.", data.personal.aadhaar],
                ["PAN Card No.", data.personal.pan],
                ["Passport No.", data.personal.passport],
            ])}

            {data.contact && renderSection("2. Contact Info", [
                ["Present Address", data.contact.presentAddress],
                ["Permanent Address", data.contact.permanentAddress],
                ["Personal Email ID", data.contact.personalEmail],
                ["Emergency Contact Name", data.contact.emergencyContactName],
                ["Emergency Relation", data.contact.emergencyContactRelation],
                ["Emergency Contact Phone", data.contact.emergencyContactPhone],
            ])}

            {data.academic && renderSection("3. Academic Background", [
                ["Degree/Class", data.academic.degree],
                ["Institute/School", data.academic.institute],
                ["Passing Year", data.academic.passingYear],
                ["Percentage/GPA", data.academic.score],
            ])}

            {data.employment && renderSection("4. Previous Employment", [
                ["Previous Company", data.employment.previousCompany],
                ["Job Title/Designation", data.employment.jobTitle],
                ["Tenure Dates", data.employment.dates],
                ["Last Drawn Salary", data.employment.lastSalary],
            ])}

            {data.dutyReport && renderSection("5. Duty Reporting", [
                ["Reporting Date", data.dutyReport.reportingDate],
                ["Reporting Dept", data.dutyReport.department],
                ["Reporting Desg", data.dutyReport.designation],
                ["Reporting Manager", data.dutyReport.manager],
                ["Office Location", data.dutyReport.officeLocation],
                ["Working Hours", data.dutyReport.workingHours],
            ])}

            {data.bankFamily && renderSection("6. Bank Details & Family Info", [
                ["Bank Name", data.bankFamily.bankName],
                ["Account Number", data.bankFamily.accountNumber],
                ["IFSC Code", data.bankFamily.ifsc],
                ["Branch Location", data.bankFamily.branch],
                ["Family Member Name", data.bankFamily.familyMemberName],
                ["Family Relation", data.bankFamily.familyMemberRelation],
                ["Family Member DOB", data.bankFamily.familyMemberDob],
            ])}

            {data.nominee && renderSection("7. Nominee", [
                ["Nominee Name", data.nominee.nomineeName],
                ["Nominee Relation", data.nominee.nomineeRelation],
                ["Share Percentage (%)", data.nominee.sharePercentage],
                ["Nominee Aadhaar No.", data.nominee.nomineeAadhaar],
            ])}

            {data.formF && renderSection("8. Form F (Gratuity)", [
                ["Gratuity Nominee Name", data.formF.gratuityNomineeName],
                ["Proportion of Share", data.formF.proportionOfShare],
                ["Gratuity Nominee Address", data.formF.gratuityNomineeAddress],
            ])}

            {data.form25 && renderSection("9. Form 25 (EPF Status)", [
                ["EPF Enrolled", data.form25.epfStatus],
                ["Previous PF Number", data.form25.previousPFNumber],
                ["UAN Number", data.form25.uanNumber],
            ])}

            {data.professional && renderSection("10. Professional References", [
                ["Reference 1 Name", data.professional.ref1Name],
                ["Ref 1 Designation", data.professional.ref1Designation],
                ["Ref 1 Phone", data.professional.ref1Phone],
                ["Reference 2 Name", data.professional.ref2Name],
                ["Ref 2 Designation", data.professional.ref2Designation],
                ["Ref 2 Phone", data.professional.ref2Phone],
            ])}

            {data.uploads && (
                <div>
                    <h4 className="font-heading text-xs font-bold text-teal-600 dark:text-teal-400 mb-3">11. Uploaded Documents</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            ["Aadhaar Card Copy", data.uploads.aadhaarFile],
                            ["PAN Card Copy", data.uploads.panFile],
                            ["Graduation Certificate", data.uploads.academicFile],
                            ["Experience Letter", data.uploads.experienceFile],
                        ].map(([label, url]) => (
                            <div key={label} className="border rounded-xl p-3 flex justify-between items-center bg-slate-50/20 dark:bg-slate-900/10">
                                <div>
                                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                                    <span className="text-[10px] text-muted-foreground truncate max-w-xs block mt-0.5">
                                        {url ? url.split("/").pop() : "No file uploaded"}
                                    </span>
                                </div>
                                {url ? (
                                    <a 
                                        href={url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-xs text-teal-600 dark:text-teal-400 hover:underline font-semibold"
                                    >
                                        View File
                                    </a>
                                ) : (
                                    <span className="text-xs text-slate-400 italic">Pending</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
